import type { OrganizationLogoSize } from '~/components/entity/organization-logo'
import { useBreakpoints } from '@vueuse/core'

// Above this count the columns get too narrow for a 32px logo to sit comfortably.
const DENSE_COLUMN_COUNT = 12

export const MONITOR_COLUMN_ANIMATION_DELAY_MS = 30

interface UseMonitorColumnsOptions<T> {
  items: ComputedRef<T[]>
  rate: (item: T) => number
  // Columns are scaled against the highest rate and floored so the cheapest
  // item still renders a visible bar instead of a sliver.
  minBarPercent: number
}

export function useMonitorColumns<T>({ items, rate, minBarPercent }: UseMonitorColumnsOptions<T>) {
  const breakpoints = useBreakpoints({
    mobile: 0,
    tablet: 640,
    desktop: 1024
  })
  const isMobile = breakpoints.smaller('tablet')

  const maxRate = computed(() =>
    items.value.length ? Math.max(...items.value.map(rate)) : 0
  )

  const barHeight = (value: number) => {
    if (!maxRate.value) return `${minBarPercent}%`

    const scaled = (value / maxRate.value) * 100

    return `${Math.max(scaled, minBarPercent)}%`
  }

  const rateFormatter = new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  })

  const formatRate = (value: number | null | undefined) =>
    value === null || value === undefined ? '—' : `${rateFormatter.format(value)}%`

  const logoSize = computed<OrganizationLogoSize>(() => {
    if (isMobile.value || items.value.length > DENSE_COLUMN_COUNT) return 'xs'

    return 'sm'
  })

  return { barHeight, formatRate, logoSize }
}
